import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { decoShapes, fonts, heartSizes } from '../constants';
import type { DecoItem, DownloadMethod, OutlineType, TextItem } from '../types';
import { downloadWithDomToImage } from '../utils/domToImageUtils';
import domtoimage from 'dom-to-image-more';

// URLパラメータをエンコードする関数（マルチバイト文字に対応）
const encodeState = (state: {
  textItems: TextItem[];
  decos: DecoItem[];
  bgColor: string; 
  fillMode: string;
}) => {
  const data = {
    t: state.textItems.map((item) => ({
      text: item.text,
      x: Math.round(item.x),
      y: Math.round(item.y),
      c: item.color,
      s: item.fontSize,
      f: item.font,
      r: item.rotate || 0,
      o: item.outlineType || 'none', // テキスト縁取りタイプ
    })),
    d: state.decos.map((deco) => ({
      x: Math.round(deco.x),
      y: Math.round(deco.y),
      c: deco.color,
      s: deco.size,
      r: deco.rotate || 0,
      t: deco.shape,
    })),
    bg: state.bgColor,
    fm: state.fillMode,
  };
  // マルチバイト文字に対応するためにencodeURIComponentを使用
  return encodeURIComponent(JSON.stringify(data));
};

// URLパラメータをデコードする関数（マルチバイト文字に対応）
const decodeState = (encoded: string) => {
  try {
    // URLパラメータをデコード
    const decodedData = decodeURIComponent(encoded);
    const data = JSON.parse(decodedData);

    return {
      textItems: (data.t || []).map((item: any) => ({
        id: uuidv4(),
        text: item.text || '',
        x: item.x || 180,
        y: item.y || 180,
        color: item.c || '#FF69B4',
        fontSize: item.s || 40,
        font: item.f || fonts[0].value,
        rotate: item.r || 0,
        outlineType: item.o || 'none',
      })),
      decos: (data.d || []).slice(0, 5).map((deco: any) => ({
        id: uuidv4(),
        x: deco.x || 180,
        y: deco.y || 180,
        color: deco.c || '#FF69B4',
        size: deco.s || 48,
        rotate: deco.r || 0,
        shape: deco.t || 'heart',
      })),
      bgColor: data.bg || '#000000',
      fillMode: data.fm || 'rounded',
    };
  } catch (e) {
    console.error('URLパラメータの解析に失敗しました', e);
    return null;
  }
};

export const useUchiwaState = () => {
  // URLパラメータからの状態復元を試みる
  const getInitialStateFromUrl = () => {
    if (typeof window !== 'undefined') {
      const urlParams = new URLSearchParams(window.location.search);
      const stateParam = urlParams.get('state');
      if (stateParam) {
        try {
          return decodeState(stateParam);
        } catch (e) {
          console.error('URLパラメータの復元に失敗しました', e);
        }
      }
    }
    return null;
  };

  const initialState = getInitialStateFromUrl();

  // 複数テキスト用の状態
  const [textItems, setTextItems] = useState<TextItem[]>(() => {
    // URLパラメータからの復元を優先
    if (initialState?.textItems && initialState.textItems.length > 0) {
      return initialState.textItems;
    }

    // ローカルストレージから (キーを変更または削除検討)
    const saved = localStorage.getItem('uchiwa_text_items');
    if (saved) {
      try {
        const parsedItems = JSON.parse(saved);
        if (Array.isArray(parsedItems) && parsedItems.length > 0) {
          // 既存のアイテムに回転角度と縁取りタイプがなければ追加
          return parsedItems.map((item: TextItem) => ({
            ...item,
            rotate: item.rotate !== undefined ? item.rotate : 0,
            outlineType: item.outlineType || 'none',
          }));
        }
      } catch {}
    }
    // デフォルトで中央に1つテキスト項目を作成
    return [
      {
        id: uuidv4(),
        text: 'テキスト', // デフォルトテキスト変更
        x: 180,
        y: 180, // 中央寄りに変更
        color: '#FF69B4',
        fontSize: 60,
        font: '"M PLUS Rounded 1c", sans-serif',
        rotate: 0,
        outlineType: 'none', // デフォルトは縁取りなし
      },
    ];
  });
  const [bgColor, setBgColor] = useState(initialState?.bgColor || '#000000');
  const [fillMode, setFillMode] = useState(initialState?.fillMode || 'rounded'); // 'uchiwa', 'rounded', 'all', 'none'
  
  // 縮小プレビューの表示状態
  const [showMiniPreview, setShowMiniPreview] = useState<boolean>(true);
  
  const [decos, setDecos] = useState<DecoItem[]>(() => {
    // URLパラメータからの復元を優先
    if (initialState?.decos) {
      return initialState.decos;
    }

    // ローカルストレージから
    const saved = localStorage.getItem('uchiwa_decos');
    if (saved) {
      try {
        const parsedDecos = JSON.parse(saved);
        if (Array.isArray(parsedDecos)) {
          return parsedDecos;
        }
      } catch {}
    }
    return [];
  });
  const [selectedHeartSize, setSelectedHeartSize] = useState<number>(48);
  const [isDownloading, setIsDownloading] = useState(false);
  // downloadMethodはdomtoimage固定
  const downloadMethod: DownloadMethod = 'domtoimage';
  const setDownloadMethod = () => {}; // 互換性のために空関数を保持
  const svgRef = useRef<SVGSVGElement>(null);
  
  // ミニプレビュー用のstate
  const [miniPreviewSrc, setMiniPreviewSrc] = useState<string>('');

  const dragDecoIndex = useRef<number | null>(null);
  const decoOffset = useRef<{ x: number; y: number }>({ x: 0, y: 0 });

  // ミニプレビューを更新する関数
  const updateMiniPreview = useCallback(() => {
    // if (!svgRef.current || !showMiniPreview) return; // showMiniPreviewの条件を削除
    if (!svgRef.current) return; // svgRefがない場合は早期リターン
    
    const tempDiv = document.createElement('div');
    tempDiv.style.position = 'absolute';
    tempDiv.style.left = '-9999px';
    tempDiv.style.top = '-9999px';
    tempDiv.style.width = '360px'; // メインSVGのサイズに合わせる
    tempDiv.style.height = '360px'; // メインSVGのサイズに合わせる
    
    const svgClone = svgRef.current.cloneNode(true) as SVGSVGElement;
    
    const deleteButtons = svgClone.querySelectorAll('.deco-delete-button');
    deleteButtons.forEach(el => el.remove());
    
    const positionMarkers = svgClone.querySelectorAll('circle[fill="rgba(255,0,0,0.5)"]');
    positionMarkers.forEach(el => el.remove());

    // クローンしたSVGのビューボックスとサイズを調整して、期待通りの範囲が描画されるようにする
    // svgClone.setAttribute('viewBox', '0 0 360 360'); // 必要に応じて調整
    // svgClone.setAttribute('width', '360');
    // svgClone.setAttribute('height', '360');
    
    tempDiv.appendChild(svgClone);
    document.body.appendChild(tempDiv);
    
    domtoimage.toPng(tempDiv)
      .then((dataUrl: string) => {
        setMiniPreviewSrc(dataUrl);
      })
      .catch((error: any) => {
        console.error('ミニプレビュー画像の生成に失敗しました:', error);
        setMiniPreviewSrc('');
      })
      .finally(() => {
        document.body.removeChild(tempDiv);
      });
  }, [svgRef]); // 依存配列は svgRef のみ

  // 状態が変更されたらミニプレビューを更新
  useEffect(() => {
    // textItems, decos, bgColor, fillMode のいずれかが変更されたらミニプレビューを更新
    updateMiniPreview();
  }, [textItems, decos, bgColor, fillMode, updateMiniPreview]);

  // 現在の状態をURLパラメータとして取得する関数
  const getShareableUrl = () => {
    const state = {
      textItems, // textItems を使用
      decos,
      bgColor,
      fillMode,
    };

    const encodedState = encodeState(state);
    const url = new URL(window.location.href);
    url.search = `?state=${encodedState}`;
    return url.toString();
  };

  // 共有用URLをクリップボードにコピー
  const copyShareableUrl = () => {
    const url = getShareableUrl();
    navigator.clipboard
      .writeText(url)
      .then(() => {
        alert('共有用URLをクリップボードにコピーしました！');
      })
      .catch(() => {
        alert('URLのコピーに失敗しました。');
      });
  };

  // パラメータのみをクリップボードにコピー
  const copyParametersOnly = () => {
    const url = getShareableUrl();
    const urlObj = new URL(url);
    // パラメータの前に「?」をつけてコピー
    const params = `?${urlObj.searchParams.toString()}`;
    navigator.clipboard
      .writeText(params)
      .then(() => {
        alert('?付きパラメータをクリップボードにコピーしました！');
      })
      .catch(() => {
        alert('パラメータのコピーに失敗しました。');
      });
  };

  // dom-to-image-moreを使用した画像ダウンロード関数
  const handleDomToImageDownload = () => {
    setIsDownloading(true);

    // テキストの内容を結合してファイル名を生成
    console.log('textItems:', textItems);
    let combinedText = textItems
      .map((item) => item.text.replace(/[\n]/g, ''))
      .join('')
      .trim();
    // ファイル名に使用できない文字を除去
    combinedText = combinedText.replace(/[/\\:*?"<>|]/g, '');
    
    // 絵文字を除去（Unicode絵文字範囲を使用）
    combinedText = combinedText.replace(
      /[\u{1F600}-\u{1F64F}\u{1F300}-\u{1F5FF}\u{1F680}-\u{1F6FF}\u{1F700}-\u{1F77F}\u{1F780}-\u{1F7FF}\u{1F800}-\u{1F8FF}\u{1F900}-\u{1F9FF}\u{1FA00}-\u{1FA6F}\u{1FA70}-\u{1FAFF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}]/gu, 
      ''
    );
    
    // ハイフン「−」（U+2212）を全角ダッシュ「ー」（U+30FC）に置換
    combinedText = combinedText.replace(/−/g, 'ー');
    
    // 長すぎる場合は短縮
    if (combinedText.length > 30) {
      combinedText = combinedText.substring(0, 30);
    }
    // 空の場合はデフォルト名
    const fileName = combinedText ? `uchiwa_${combinedText}.png` : 'uchiwa.png';

    downloadWithDomToImage(
      '.preview-svg-container',
      fillMode === 'none' ? '#ffffff' : null,
      fileName // 生成したファイル名を使用
    )
      .then(() => {
        setIsDownloading(false);
      })
      .catch((error) => {
        console.error('画像生成エラー:', error);
        alert('画像の生成に失敗しました。別の方法で試してください。');
        setIsDownloading(false);
      });
  };

  // handleDownload を簡略化
  const handleDownload = () => {
    // dom-to-image のみ実行
    handleDomToImageDownload();
  };

  // ローカルストレージ保存・復元ロジックを修正
  useEffect(() => {
    // uchiwa_all の読み込みを削除
    // const saved = localStorage.getItem('uchiwa_all');
    // ...
  }, []);

  useEffect(() => {
    // uchiwa_all の保存を削除
    // localStorage.setItem('uchiwa_all', JSON.stringify({ ... }));
  }, []); // 依存配列を空にするか、関連する状態に変更

  useEffect(() => {
    localStorage.setItem('uchiwa_text_items', JSON.stringify(textItems));
  }, [textItems]);

  useEffect(() => {
    localStorage.setItem('uchiwa_decos', JSON.stringify(decos));
  }, [decos]);

  // 図形追加 (addDeco) は変更なし
  const addDeco = (shape: DecoItem['shape'], color: string, rotate = 0) => {
    // 5つ以上の図形がある場合は追加しない
    if (decos.length >= 5) {
      alert('図形は最大5つまで追加できます');
      return;
    }
    setDecos([
      ...decos,
      { x: 180, y: 200, color, size: selectedHeartSize, id: uuidv4(), rotate, shape },
    ]);
  };

  // 図形のドラッグ移動 (handleDecoMouseDown, onDecoMouseMove, onDecoMouseUp) は変更なし
  const handleDecoMouseDown = (index: number) => (e: React.MouseEvent<SVGGElement, MouseEvent>) => {
    dragDecoIndex.current = index;
    decoOffset.current = {
      x: e.clientX - decos[index].x,
      y: e.clientY - decos[index].y,
    };
    document.addEventListener('mousemove', onDecoMouseMove);
    document.addEventListener('mouseup', onDecoMouseUp);
  };

  const onDecoMouseMove = (e: MouseEvent) => {
    if (dragDecoIndex.current === null) return;
    setDecos((prev) => {
      const newDecos = [...prev];
      newDecos[dragDecoIndex.current!] = {
        ...newDecos[dragDecoIndex.current!],
        x: e.clientX - decoOffset.current.x,
        y: e.clientY - decoOffset.current.y,
      };
      return newDecos;
    });
  };

  const onDecoMouseUp = () => {
    dragDecoIndex.current = null;
    document.removeEventListener('mousemove', onDecoMouseMove);
    document.removeEventListener('mouseup', onDecoMouseUp);
  };

  // 図形一括削除
  const clearDecos = () => setDecos([]);

  // 図形をランダム配置（5つまでに制限）
  const addRandomDecos = (count = 8) => {
    const maxAddableItems = Math.max(0, 5 - decos.length);
    if (maxAddableItems <= 0) {
      alert('図形は最大5つまで追加できます');
      return;
    }
    const actualCount = Math.min(count, maxAddableItems);

    const minX = 50;
    const maxX = 310;
    const minY = 60;
    const maxY = 320;
    const newDecos: DecoItem[] = [];
    for (let i = 0; i < actualCount; i++) {
      const x = Math.random() * (maxX - minX) + minX;
      const y = Math.random() * (maxY - minY) + minY;
      // TODO: 必要であれば textItems の領域との衝突判定を追加
      const deco = decoShapes[Math.floor(Math.random() * decoShapes.length)];
      const rotate = [-15, 0, 15][Math.floor(Math.random() * 3)];
      const size = heartSizes[Math.floor(Math.random() * heartSizes.length)].value;
      newDecos.push({
        x,
        y,
        color: deco.color,
        size,
        id: uuidv4(),
        rotate,
        shape: deco.shape as DecoItem['shape'],
      });
    }
    setDecos([...decos, ...newDecos]);
  };

  // 図形ごとにランダム配置（5つまでに制限）
  const addRandomDecosByShape = (shape: DecoItem['shape'], count = 8) => {
    const maxAddableItems = Math.max(0, 5 - decos.length);
    if (maxAddableItems <= 0) {
      alert('図形は最大5つまで追加できます');
      return;
    }
    const actualCount = Math.min(count, maxAddableItems);

    const minX = 50;
    const maxX = 310;
    const minY = 60;
    const maxY = 320;
    const newDecos: DecoItem[] = [];
    const decoColor = decoShapes.find((d) => d.shape === shape)?.color || '#FF4081';
    for (let i = 0; i < actualCount; i++) {
      const x = Math.random() * (maxX - minX) + minX;
      const y = Math.random() * (maxY - minY) + minY;
      // TODO: 必要であれば textItems の領域との衝突判定を追加
      const rotate = [-15, 0, 15][Math.floor(Math.random() * 3)];
      const size = heartSizes[Math.floor(Math.random() * heartSizes.length)].value;
      newDecos.push({ x, y, color: decoColor, size, id: uuidv4(), rotate, shape });
    }
    setDecos([...decos, ...newDecos]);
  };

  // テキスト項目を追加する
  const addTextItem = () => {
    // 5つまで追加可能 
    if (textItems.length >= 5) return;

    // より視覚的に均等な配置にするための調整値
    const centerX = 180;
    const centerY = 180;
    const radius = 70; // 中央からの配置半径

    // 位置をテキスト数に応じて最適な配置で計算
    let newX = centerX;
    let newY = centerY;

    switch (textItems.length) {
      case 0:
        // 1つ目：中央
        newX = centerX;
        newY = centerY;
        break;
      case 1:
        // 2つ目：上部
        newX = centerX;
        newY = centerY - radius;
        break;
      case 2:
        // 3つ目：右側
        newX = centerX + radius;
        newY = centerY;
        break;
      case 3:
        // 4つ目：下部
        newX = centerX;
        newY = centerY + radius;
        break;
      case 4:
        // 5つ目：左側
        newX = centerX - radius;
        newY = centerY;
        break;
      default:
        // 念のため
        newX = centerX;
        newY = centerY;
    }

    // 最後のテキストアイテムから設定を引き継ぐ
    let color = '#FF69B4'; // デフォルト色
    let fontSize = 40; // デフォルトフォントサイズ
    let font = '"M PLUS Rounded 1c", sans-serif'; // デフォルトフォント
    let outlineType: OutlineType = 'none'; // デフォルトの縁取りタイプ

    if (textItems.length > 0) {
      // 配列の最後の要素から設定を取得
      const lastItem = textItems[textItems.length - 1];
      color = lastItem.color;
      fontSize = lastItem.fontSize;
      font = lastItem.font;
      outlineType = lastItem.outlineType || 'none';
    }

    setTextItems([
      ...textItems,
      {
        id: uuidv4(),
        text: '',
        x: newX,
        y: newY,
        color, // 最後のアイテムから引き継いだ色
        fontSize, // 最後のアイテムから引き継いだフォントサイズ
        font, // 最後のアイテムから引き継いだフォント
        outlineType, // 最後のアイテムから引き継いだ縁取りタイプ
        rotate: 0,
      },
    ]);
  };

  // テキスト項目を更新する
  const updateTextItem = (id: string, updates: Partial<TextItem>) => {
    setTextItems(textItems.map((item) => (item.id === id ? { ...item, ...updates } : item)));
  };

  // テキスト項目を削除する
  const removeTextItem = (id: string) => {
    setTextItems(textItems.filter((item) => item.id !== id));
  };

  // テキストアイテムの位置を移動する
  const handleTextDragStart = (id: string) => (e: React.MouseEvent<SVGGElement, MouseEvent>) => {
    const item = textItems.find((item) => item.id === id);
    if (!item) return;

    const dragOffset = {
      x: e.clientX - item.x,
      y: e.clientY - item.y,
    };

    const onMouseMove = (moveEvent: MouseEvent) => {
      setTextItems((prev) =>
        prev.map((textItem) =>
          textItem.id === id
            ? {
                ...textItem,
                x: moveEvent.clientX - dragOffset.x,
                y: moveEvent.clientY - dragOffset.y,
                rotate: textItem.rotate || 0, // rotateプロパティがなければ0を設定
              }
            : textItem
        )
      );
    };

    const onMouseUp = () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  };

  // リセット機能 (resetAllSettings) を修正
  const resetAllSettings = () => {
    if (
      window.confirm('すべての設定をリセットし、トップページに戻りますか？変更内容は失われます。')
    ) {
      // 単一テキスト関連のデフォルト値設定を削除
      // setTextColor('#FF69B4');
      // setFont('"M PLUS Rounded 1c", sans-serif');
      // setFontSize(60);

      // bgColor と fillMode はリセット
      setBgColor('#000000');
      setFillMode('rounded');

      // デフォルトのテキストアイテムを1つ設定 (初期化ロジックと合わせる)
      setTextItems([
        {
          id: uuidv4(),
          text: 'テキスト',
          x: 180,
          y: 180,
          color: '#FF69B4',
          fontSize: 60,
          font: '"M PLUS Rounded 1c", sans-serif',
          rotate: 0,
          outlineType: 'none', // デフォルトは縁取りなし
        },
      ]);

      // 図形をクリア
      setDecos([]);
      // setHearts([]); // hearts は削除済み

      // URLをトップに戻す
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  };

  // 縮小プレビューの表示/非表示を切り替える関数
  const toggleMiniPreview = useCallback(() => {
    setShowMiniPreview(prev => !prev);
  }, []);

  return {
    // 維持する状態と関数
    bgColor,
    setBgColor,
    fillMode,
    setFillMode,
    textItems,
    setTextItems,
    addTextItem,
    updateTextItem,
    removeTextItem,
    handleTextDragStart,
    decos,
    setDecos,
    selectedHeartSize,
    setSelectedHeartSize,
    isDownloading,
    svgRef,
    // miniPreviewRef, // 削除
    handleDownload,
    downloadMethod,
    setDownloadMethod,
    addDeco,
    handleDecoMouseDown,
    // removeDeco, // 未使用のため削除
    clearDecos,
    addRandomDecos,
    addRandomDecosByShape,
    copyShareableUrl,
    copyParametersOnly,
    resetAllSettings,
    showMiniPreview,
    setShowMiniPreview,
    toggleMiniPreview,
    miniPreviewSrc, // App.tsxで使えるように返す
  };
};
